import { apiWorkflowFolders } from "@/api/workflows/workflow-folder.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ApiErrorException } from "@/types/api.types.ts";
import { ApiWorkflowFolderPayload } from "@/types/workflows/workflow-folder.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Control, FieldValues } from "react-hook-form";

export const workflowFoldersKeys = createQueryKeys("workflowFolders", {
  all: {
    queryKey: ["all"],
    queryFn: async ({ signal }) => {
      const result = await apiWorkflowFolders.getAll(signal);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  },
});

export const useWorkflowFolders = (enabled = true) => {
  return useQuery({ ...workflowFoldersKeys.all, enabled });
};

interface WorkflowFolderSaveParams {
  id?: number;
  payload: ApiWorkflowFolderPayload;
}

export const useWorkflowFolderSave = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ id, payload }: WorkflowFolderSaveParams) => {
      const result = id
        ? await apiWorkflowFolders.update(id, payload)
        : await apiWorkflowFolders.create(payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: workflowFoldersKeys.all._def });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useWorkflowFolderDelete = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ id }: { id: number }) => {
      const result = await apiWorkflowFolders.delete(id);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: workflowFoldersKeys.all._def });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};
